import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarDays } from "lucide-react";

// Day codes come from the backend (register_codes.py). Colours: worked = green,
// on duty = sky, paid leave = indigo, half day = amber, unpaid = orange/red, off days = grey.
const LEAVE_NAMES = { CL: "Casual Leave", PL: "Privilege / Earned Leave", EL: "Earned Leave", CLC: "Casual Leave (Contract / Trainee)",
  COMP: "Compensatory Off", ML: "Maternity Leave", PAT: "Paternity Leave", RH: "Restricted Holiday" };

function codeClass(c) {
  if (!c) return "text-slate-300";
  if (c === "P") return "bg-emerald-50 text-emerald-700";
  if (c === "OD") return "bg-sky-50 text-sky-700";
  if (c === "WO") return "bg-slate-100 text-slate-500";
  if (c === "H") return "bg-violet-50 text-violet-700";
  if (c === "LOP" || c === "½LOP" || c === "½OD") return "bg-red-50 text-red-700 font-semibold";
  if (c === "LWP" || c === "½LWP" || c === "HD") return "bg-orange-50 text-orange-700 font-semibold";
  if (c.startsWith("½")) return "bg-amber-50 text-amber-800";
  if (c === "NJ" || c === "EX") return "bg-white text-slate-300";
  if (c === "·") return "text-slate-400";
  return "bg-indigo-50 text-indigo-700"; // a paid leave type: CL, PL, CLC, COMP, ML, RH…
}

const LEGEND = [
  ["P", "Present"], ["OD", "On Duty"], ["WO", "Weekly Off"], ["H", "Holiday"],
  ["CL", "Paid leave (code = leave type: CL, PL, CLC, COMP…)"], ["½CL", "Half Day — half worked, half that leave"],
  ["LWP", "Leave Without Pay (applied)"], ["½LWP", "Half Day — unpaid half"],
  ["LOP", "Loss of Pay — absent, nothing applied"], ["NJ", "Not yet joined"], ["·", "Today, not yet marked"],
];

const fmt = (n) => (n ? Number(n).toString() : "0");

function Num({ v, tone }) {
  return <span className={v > 0 ? tone : "text-slate-400"}>{fmt(v)}</span>;
}

function LeaveChips({ leaves }) {
  const entries = Object.entries(leaves || {});
  if (!entries.length) return <span className="text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([code, days]) => (
        <span key={code} title={LEAVE_NAMES[code] || code}
          className="inline-flex items-center rounded px-1.5 py-0.5 text-xs bg-indigo-50 text-indigo-700 font-medium">
          {code} {fmt(days)}
        </span>
      ))}
    </div>
  );
}

function SummaryTable({ rows }) {
  const head = "overline text-slate-500 whitespace-nowrap";
  return (
    <Card className="border-slate-200 shadow-none overflow-x-auto">
      <Table>
        <TableHeader><TableRow className="bg-slate-50">
          <TableHead className={head}>Employee</TableHead>
          <TableHead className={head} title="Days that count as working days so far (excludes weekly offs and holidays)">Working Days</TableHead>
          <TableHead className={head}>Present</TableHead>
          <TableHead className={head}>On Duty</TableHead>
          <TableHead className={head}>Weekly Off</TableHead>
          <TableHead className={head}>Holiday</TableHead>
          <TableHead className={head}>Paid Leave (by type)</TableHead>
          <TableHead className={head}>Half Days</TableHead>
          <TableHead className={head} title="Leave Without Pay that was applied for">LWP</TableHead>
          <TableHead className={head} title="Loss of Pay: absent with no leave applied">LOP</TableHead>
          <TableHead className={head}>Grace Used</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => {
            const b = r.breakdown || {};
            return (
              <TableRow key={r.employee_id} className="table-row-i">
                <TableCell className="font-medium text-navy whitespace-nowrap">{r.employee_name} <span className="text-slate-400 font-mono-i text-xs">({r.employee_code})</span></TableCell>
                <TableCell className="text-slate-600">{fmt(r.working_days)}</TableCell>
                <TableCell><Num v={b.present} tone="text-emerald-700 font-medium" /></TableCell>
                <TableCell><Num v={b.od} tone="text-sky-700 font-medium" /></TableCell>
                <TableCell><Num v={b.weekly_offs} tone="text-slate-600" /></TableCell>
                <TableCell><Num v={b.holidays} tone="text-violet-700" /></TableCell>
                <TableCell><LeaveChips leaves={b.leaves} /></TableCell>
                <TableCell><Num v={b.half_days} tone="text-amber-700 font-medium" /></TableCell>
                <TableCell><Num v={b.lwp} tone="text-orange-700 font-semibold" /></TableCell>
                <TableCell><Num v={b.lop} tone="text-red-600 font-semibold" /></TableCell>
                <TableCell className="text-slate-600">{r.grace_incidents_used} / 2</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

function DayGrid({ rows, month }) {
  const days = rows[0]?.days || [];
  const wk = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  return (
    <Card className="border-slate-200 shadow-none overflow-x-auto">
      <table className="text-xs border-collapse min-w-full">
        <thead>
          <tr className="bg-slate-50">
            <th className="sticky left-0 z-10 bg-slate-50 text-left px-3 py-2 overline text-slate-500 min-w-[180px]">Employee</th>
            {days.map((d) => {
              const dt = new Date(`${d.date}T00:00:00`);
              return (
                <th key={d.date} className="px-0.5 py-1 font-medium text-slate-500 text-center min-w-[34px]">
                  <div>{dt.getDate()}</div>
                  <div className="text-[10px] text-slate-400 font-normal">{wk[dt.getDay()]}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.employee_id} className="border-t border-slate-100">
              <td className="sticky left-0 z-10 bg-white px-3 py-1.5 font-medium text-navy whitespace-nowrap">
                {r.employee_name} <span className="text-slate-400 font-mono-i">({r.employee_code})</span>
              </td>
              {(r.days || []).map((d) => (
                <td key={d.date} className="px-0.5 py-1 text-center">
                  <span title={`${d.date}: ${d.status}${d.note ? ` — ${d.note}` : ""}`}
                    className={`inline-block min-w-[30px] rounded px-1 py-0.5 leading-4 ${codeClass(d.code)}`}>
                    {d.code || " "}{d.note ? "*" : ""}
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-slate-400 px-3 py-2 border-t border-slate-100">
        Hover over any day for its full status. * = check-out missing (becomes Half Day unless corrected). {month}
      </p>
    </Card>
  );
}

export default function MonthlyRegister() {
  const thisMonth = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(thisMonth);
  const { data: register, isLoading } = useQuery({
    queryKey: ["attRegister", month],
    queryFn: async () => (await api.get(`/attendance/admin/register?month=${month}`)).data,
  });
  const rows = register?.employees || [];

  return (
    <>
      <PageHeader overline="Attendance" title="Monthly Register" icon={CalendarDays}
        subtitle={`Present, On Duty, weekly offs, holidays, each leave type, LWP and LOP for ${month}${month === thisMonth ? " (so far)" : ""} — for HR/payroll review.`}
        actions={<Input type="month" value={month} max={thisMonth} onChange={(e) => e.target.value && setMonth(e.target.value)}
          className="w-40 bg-white" data-testid="register-month" />} />
      {isLoading ? <Loading /> : rows.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No attendance recorded for this month" />
      ) : (
        <Tabs defaultValue="summary" className="mb-8">
          <TabsList className="mb-3">
            <TabsTrigger value="summary" data-testid="register-tab-summary">Summary</TabsTrigger>
            <TabsTrigger value="daywise" data-testid="register-tab-daywise">Day-wise</TabsTrigger>
          </TabsList>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5 mb-3 text-xs text-slate-600" data-testid="register-legend">
            {LEGEND.map(([c, label]) => (
              <span key={c} className="inline-flex items-center gap-1">
                <span className={`inline-block min-w-[30px] text-center rounded px-1 py-0.5 ${codeClass(c)}`}>{c}</span>{label}
              </span>
            ))}
          </div>
          <TabsContent value="summary"><SummaryTable rows={rows} /></TabsContent>
          <TabsContent value="daywise"><DayGrid rows={rows} month={month} /></TabsContent>
        </Tabs>
      )}
    </>
  );
}
